import { Auth0AccountRole } from "client/openapi";
import type { PersonRow } from "client/openapi/models/PersonRow";
import { Dialog, DialogContent } from "components/Dialog";
import { ReactNode } from "react";

/**
 * A form sent and not filled out yet, as the People panel's Onboarding row
 * lists it. For a student it can be a form sent to one of their parents
 * (sent_to says who has it).
 */
export type OutstandingForm = {
  send_id: number;
  form_id: number;
  form_name: string;
  /** When it was last sent. */
  sent_at: string;
  /** When the link stops working, if the row says. */
  expires_at?: string | null;
  sent_to: {
    kind: string;
    id: number;
    first_name?: string | null;
    last_name?: string | null;
  };
  /**
   * A resend can go by text, by email: they (or, for a student, a parent of
   * theirs) have a phone number, an email on file. Unsaid counts as yes.
   */
  can_text?: boolean;
  can_email?: boolean;
};

/** What the People panel passes each Onboarding step dialog. */
export type StepDialogProps = {
  orgId: number;
  person: PersonRow;
  /** The step is done. */
  done: boolean;
  /** Something changed (a form sent, a plan saved): reload the row. */
  onDone: () => void;
  onClose: () => void;
};

/** The forms API's word for each kind of person. */
export const ROLE_OF: Record<string, Auth0AccountRole> = {
  student: Auth0AccountRole.ME,
  parent: Auth0AccountRole.PARENT,
  tutor: Auth0AccountRole.ORG_TUTOR,
};

/** "Kim Sample (parent)": who a form was sent to. */
export function nameAndRole(person: OutstandingForm["sent_to"]): string {
  return `${fullName(person) || "Someone"} (${person.kind})`;
}

export function fullName(person: {
  first_name?: string | null;
  last_name?: string | null;
}): string {
  return [person.first_name, person.last_name].filter(Boolean).join(" ");
}

/** A form link works two weeks from when it was last sent. */
const LINK_DAYS = 14;

export function linkExpired(form: OutstandingForm, now = new Date()): boolean {
  const expires = form.expires_at
    ? new Date(form.expires_at)
    : new Date(new Date(form.sent_at).getTime() + LINK_DAYS * 86400000);
  return expires <= now;
}

/** One step's window: its title, who it's about, and its content. */
export function StepFrame({
  title,
  person,
  onClose,
  className = "",
  children,
}: {
  title: string;
  person: PersonRow;
  onClose: () => void;
  /** More classes for the window. */
  className?: string;
  children: ReactNode;
}) {
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className={`dialog-content !max-w-[560px] ${className}`}>
        <h2 className="pr-8 text-xl font-semibold text-gray-900">{title}</h2>
        <p className="mt-0.5 text-sm text-gray-600">{fullName(person)}</p>
        <div className="mt-5">{children}</div>
      </DialogContent>
    </Dialog>
  );
}
